import { createContext, ReactNode, use, useCallback, useEffect, useState } from 'react';
import { DateString, todayDate } from '../../../common/src/date_string.ts';
import { getReaderInstant } from '../../../common/src/passed_sunday_clock.ts';

export const CurrentDateContext = createContext<DateString | undefined>(undefined);

// For tests and stories only: the instant the reader reads at, beside the date CurrentDateContext injects, for a
// scenario that needs a particular hour (a Monday before noon UTC, when the Sunday before is not yet written down,
// issue #4192). The app provides none.
export const CurrentInstantContext = createContext<Date | undefined>(undefined);

export function useCurrentDate(): DateString {
  return use(CurrentDateContext) ?? todayDate();
}

// A function giving the instant the reader reads at, as of each call, for the passed Sunday's fill window
// (getFillStartSunday): the injected CurrentInstantContext instant when there is one, else getReaderInstant of the
// current date, which is the wall clock in the app and local noon of an injected date, so a story or test does not
// depend on when it runs.
export function useReaderInstant(): () => Date {
  const today = useCurrentDate();
  const injected = use(CurrentInstantContext);
  return useCallback(() => injected ?? getReaderInstant(today), [injected, today]);
}

export const CurrentDateProvider = ({ children }: { children: ReactNode }) => {
  const [liveDate, setLiveDate] = useState<DateString>(() => todayDate());

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNextUpdate = () => {
      const now = new Date();
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      timeoutId = setTimeout(() => {
        setLiveDate(todayDate());
        scheduleNextUpdate();
      }, nextMidnight.getTime() - now.getTime());
    };

    // setTimeout is unreliable across tab suspension (especially on mobile); re-check on wake.
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      setLiveDate(todayDate());
      clearTimeout(timeoutId);
      scheduleNextUpdate();
    };

    scheduleNextUpdate();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return <CurrentDateContext value={liveDate}>{children}</CurrentDateContext>;
};
